import { observer } from 'mobx-react-lite'
import { links } from '@/utils/link'
import {
  formatSidePanelStatus,
  formatStatus,
  formatStatusTone,
  formatTimeOpen,
} from '@/features/admin/scope-change-requests/scope-change-request-formatters'
import { ScopeChangeRequestDetailsPanel } from '@/features/admin/scope-change-requests/components/ScopeChangeRequestDetailsPanel'
import { InfoSection } from './InfoSection'
import { ResponsesSection } from './ResponsesSection'
import { DetailsFooter } from './DetailsFooter'
import { BundlesSectionContainer } from '@/features/admin/scope-change-requests/components/ScopeChangeRequestDetailsPanel/ItemsSection/BundlesSection/BundlesSectionContainer'
import { InventoryItemsSectionContainer } from '@/features/admin/scope-change-requests/components/ScopeChangeRequestDetailsPanel/ItemsSection/InventoryItemsSection/InventoryItemsSectionContainer'
import { CustomItemsSectionContainer } from '@/features/admin/scope-change-requests/components/ScopeChangeRequestDetailsPanel/ItemsSection/CustomItemsSection/CustomItemsSectionContainer'
import { type ScopeChangeRequestDetailsViewModel } from './ScopeChangeRequestDetailsViewModel'

export interface ScopeChangeRequestDetailsPanelContainerProps {
  vm: ScopeChangeRequestDetailsViewModel
}

export const ScopeChangeRequestDetailsPanelContainer = observer(
  function ScopeChangeRequestDetailsPanelContainer({
    vm,
  }: ScopeChangeRequestDetailsPanelContainerProps) {
    if (!vm.modal.showing) {
      return null
    }

    const scopeChangeRequest = vm.scopeChangeRequest
    const priceBreakDown = vm.scopeChangeRequestPriceBreakDown

    return (
      <ScopeChangeRequestDetailsPanel onClose={vm.modal.close}>
        <InfoSection
          jobDisplayName={scopeChangeRequest.job.displayName}
          jobShortId={scopeChangeRequest.job.shortId}
          legendName={scopeChangeRequest.job.legendName}
          workspaceName={scopeChangeRequest.workspace.name}
          createdByName={scopeChangeRequest.createdBy.displayName}
          createTime={scopeChangeRequest.createTime}
          open={formatTimeOpen(scopeChangeRequest.createTime)}
          bundleInfo={vm.jobBundle ? vm.bundleInfo : null}
          isRejected={scopeChangeRequest.isRejected}
          priceBreakDown={{
            previousTotalPrice: priceBreakDown.previousTotalPrice,
            totalPriceDiff: priceBreakDown.totalPriceDiff,
            newTotalPrice: priceBreakDown.newTotalPrice,
            previousTotalProPrice: priceBreakDown.previousTotalProPrice,
            totalProPriceDiff: priceBreakDown.totalProPriceDiff,
            newTotalProPrice: priceBreakDown.newTotalProPrice,
          }}
          percentageNote={vm.percentageChangeApplied ? vm.percentageNote : null}
          statusTone={formatStatusTone(scopeChangeRequest.status)}
          statusText={formatStatus(scopeChangeRequest)}
          sidePanelStatusText={formatSidePanelStatus(scopeChangeRequest)}
        />
        <ResponsesSection
          responses={
            scopeChangeRequest.hasResponders
              ? scopeChangeRequest.responders.map((response) => ({
                  createdByUser: {
                    displayName: response.createdByUser.displayName,
                    avatar: response.createdByUser.avatar,
                    avatarFallback: response.createdByUser.avatarFallback,
                  },
                  responderUser: response.responderType,
                  reason: response.reason,
                  displayResponseType: response.responseType,
                  dateCreated: response.dateCreated,
                }))
              : []
          }
          requestReason={{
            createdBy: {
              displayName: scopeChangeRequest.createdBy.displayName,
              avatar: scopeChangeRequest.createdBy.avatar,
              avatarFallback: scopeChangeRequest.createdBy.avatarFallback,
            },
            collaboratorType: scopeChangeRequest.reasonCollaboratorType,
            reason: scopeChangeRequest.reason,
            createTime: scopeChangeRequest.createTime,
          }}
        />
        <BundlesSectionContainer vm={vm} />
        <InventoryItemsSectionContainer vm={vm} />
        <CustomItemsSectionContainer vm={vm} />
        <DetailsFooter
          jobLink={links.job(scopeChangeRequest.job.id)}
          isJobClosed={vm.isJobClosed}
          canResolveScopeChangeRequest={vm.canResolveScopeChangeRequest}
          isResolved={scopeChangeRequest.isResolved}
          isPendingClientApproval={scopeChangeRequest.isPendingClientApproval}
          isPendingFinalization={scopeChangeRequest.isPendingFinalization}
          isPendingAdminApproval={scopeChangeRequest.isPendingAdminApproval}
          onDecline={vm.decline}
          onFinalize={vm.finalize}
          isFinalizePending={vm.finalize.pending}
          onAdminApprove={vm.adminApprove}
          isAdminApprovePending={vm.adminApprove.pending}
          onResolve={vm.resolve}
        />
      </ScopeChangeRequestDetailsPanel>
    )
  },
)
